import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Tenant } from '../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TenantsService } from '../../../services/tenants.service';
import { MessageService } from 'primeng/api';
import { DecodersService } from '../../../services/decoders.service';
import { Decoder } from '../../../model/decoder';
import { DecoderPersistRequest } from '../../../model/requests/decoder-persist-request';
import { noSpecialCharacters } from '../../../shared/custom-validators';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard } from '../../../common/auth-guard.service';
import { CommonMessagesService } from "../../../services/common-messages.service";
import { Subject, Subscription } from "rxjs";
import { debounceTime, switchMap, tap } from "rxjs/operators";

@Component({
  selector: 'app-decoders-form',
  templateUrl: './decoders-form.component.html',
  styleUrls: ['./decoders-form.component.scss']
})
export class DecodersFormComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  public decoder: Decoder;

  public types: string[];
  public tenants: Tenant[];

  public decoderForm: FormGroup;
  public persistingChanges: any;
  public sourceVisible: boolean;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: {tenantUuid: string, oldName: string};

  public scriptsNames: string[];
  public filteredScriptsNames: string[];

  public nameDebouncer = new Subject<string>();
  private lastLoadedName: string;

  private debouncerSubscription: Subscription;
  private nameChangesSubscription: Subscription | undefined;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private decodersService: DecodersService,
              private tenantsService: TenantsService,
              private messageService: MessageService,
              private commonMessagesService: CommonMessagesService) {
    this.types = ['jar', 'js', 'process'];
  }

  ngOnInit(): void {
    this.decodersService.getDecoderScripts().subscribe(scriptsNames => {
      this.scriptsNames = scriptsNames;
      this.filteredScriptsNames = scriptsNames;
    });
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
      }
    });
    this.debouncerSubscription = this.nameDebouncer.pipe(
      debounceTime(500),
      tap( name => this.lastLoadedName = name),
      switchMap(name => this.decodersService.getDecoderScript(name))
    ).subscribe(script => {
        this.decoderForm.get('source').setValue(script);
    });
  }

  loadCreationForm(): void {
    this.decoder = new Decoder();
    if (!this.authGuard.superAdminRequired()) {
      this.decoder.tenant = this.tenants[0];
    }
    this.createForm(this.decoder);
  }

  loadEditionForm(tenantUuid, name): void {
    this.tenantsService.getTenantDecoder(tenantUuid, name).toPromise().then(response => {
      this.decoder = response.decoder;
      this.tenants = [this.decoder.tenant];
      this.editInfo = {tenantUuid: this.decoder.tenant.uuid, oldName: this.decoder.name};
      this.createForm(this.decoder);
    });
  }

  private createForm(decoder: Decoder): void {
    this.decoderForm = this.formBuilder.group({
      name: [decoder.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      type: [decoder.type, [Validators.required, Validators.maxLength(255)]],
      source: [decoder.source, [Validators.maxLength(255)]],
      routingKey: [decoder.routingKey, [Validators.required, Validators.maxLength(255)]],
      tenant: [decoder.tenant, [Validators.required]]});
    this.decoderForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.decoderForm.get('tenant').disable();
    }

    if (this.mode === 'edit') {
      this.decoderForm.get('tenant').disable();
      this.typeChanged({value: decoder.type});
    }

    this.nameChangesSubscription = this.decoderForm.get('name').valueChanges.subscribe(value => {
      this.loadScriptIfRequired(value);
    });
  }

  loadScriptIfRequired(scriptName: string) {
    if( !this.sourceVisible || this.lastLoadedName === scriptName ) return;
    this.nameDebouncer.next(scriptName);
  }

  searchScript(event) {
    const query = event.query;
    this.filteredScriptsNames = this.scriptsNames.filter(name => name.toLowerCase().includes(query.toLowerCase()));
  }

  typeChanged(event: any): void {
    if (event.value === 'js') {
      this.sourceVisible = true;
      this.loadScriptIfRequired(this.decoderForm.get('name').value);
    } else {
      this.sourceVisible = false;
      this.decoderForm.get('source').setValue(null);
    }

    if (event.value === 'js') {
      this.decoderForm.get('routingKey').disable();
      this.decoderForm.get('routingKey').setValue('.sandboxjs');
    } else if (event.value === 'jar') {
      this.decoderForm.get('routingKey').disable();
      this.decoderForm.get('routingKey').setValue('.rawmessage');
    } else if (event.value === 'process') {
      this.decoderForm.get('routingKey').enable();
    }
  }

  persistDecoderData(): void {
    if (!this.decoderForm.valid) { return; }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    if (!this.processIsValid(persistData)) {
      const summary = this.i18n.translate('error.summary.invalidField');
      const detail = this.i18n.translate('error.decoder.invalidRoutingKey');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
      this.persistingChanges = false;
      return;
    }

    let promise;
    if (this.mode === 'create') {
      promise = this.decodersService.createDecoder(persistData).toPromise();
    } else {
      promise = this.decodersService.editDecoder(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/decoders`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): DecoderPersistRequest {
    const formValue = this.decoderForm.getRawValue();
    return {
      name: formValue.name,
      type: formValue.type,
      source: formValue.source,
      routingKey: formValue.routingKey,
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null
    };
  }

  private processIsValid(persistData: DecoderPersistRequest): boolean { // TODO: validate with Angular forms if possible
    return !(persistData.type === 'process' && persistData.routingKey === '.rawmessage');
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.decoderForm.untouched;
  }

  ngOnDestroy() {
    this.debouncerSubscription.unsubscribe();
    this.nameChangesSubscription?.unsubscribe();
  }

}
